import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { createRestClient } from '@/contexts/QueryProvider'
import { useGetSearchParam } from '@/hooks'
import { useSessionStorage } from '@/hooks'
import { PersonLockRegular, SpinnerIosRegular } from '@fluentui/react-icons'
import { parseJwt } from '@/util'
import { PWC_ID_CONFIG } from '@/const'
import { ConfirmDialog } from '@/ui/Dialog/ConfirmDialog'

export const LoginPage = ({ redirectPath }: { redirectPath?: string }) => {
  const navigate = useNavigate()

  const authCode = useGetSearchParam('code')
  const issuer = useGetSearchParam('iss')
  const clientId = useGetSearchParam('client_id')
  const state = useGetSearchParam('state')
  const redirectUri = `${window.location.origin}/oauth`

  const { postAuthenticationApiGetToken } = createRestClient()
  const { mutate: getToken, isPending, isIdle } = postAuthenticationApiGetToken.useMutation()
  const [, setToken] = useSessionStorage('token', '')
  const [, setSub] = useSessionStorage('sub', 0)
  const [, setRoles] = useSessionStorage('roles', 1)
  const [forceLoginDialogOpen, setForceLoginDialogOpen] = useState(false)

  // const forceSessionWithRedirectPath = redirectPath
  //   ? JSON.stringify({ ...JSON.parse(redirectPath), forceNewSession: true })
  //   : JSON.stringify({ forceNewSession: true })

  const login = useCallback(
    ({
      authCode,
      clientId,
      issuer,
      forceNewSession = false,
    }: {
      authCode?: string
      clientId?: string
      issuer?: string
      forceNewSession: boolean
    }) => {
      if (!authCode || !issuer || !clientId) {
        return
      }
      navigate({ search: '' })
      getToken(
        {
          query: {
            authCode,
            redirectUri,
            clientId,
            issuer,
            forceNewSession,
          },
        },
        {
          onSuccess: (data) => {
            setToken(data.token)
            const jwt = parseJwt(data.token)
            const sub = jwt.sub
            const roles = jwt.roles
            setSub(sub)
            setRoles(roles)
            if (!state) {
              navigate({ pathname: '/workspaces', search: 'authStatus=login-success' })
            } else {
              const parsedState = JSON.parse(state)
              navigate({
                pathname: parsedState.pathname ?? '/workspaces',
                search: new URLSearchParams(parsedState.search).toString(),
              })
            }
          },
          onError: (error) => {
            console.error(error)
            if (forceNewSession) {
              navigate({ pathname: '/workspaces', search: 'authStatus=login-failed' })
            } else {
              setForceLoginDialogOpen(true)
            }
          },
        }
      )
    },
    [getToken, navigate, redirectUri, setRoles, setSub, setToken, state]
  )

  useEffect(() => {
    const forceNewSession = state ? ((JSON.parse(state).forceNewSession as boolean | undefined) ?? false) : false
    if (!forceLoginDialogOpen) {
      login({ authCode, clientId, issuer, forceNewSession: forceNewSession })
    } else {
      navigate({ search: '' })
    }
  }, [authCode, clientId, forceLoginDialogOpen, issuer, login, navigate, state])

  return (
    <>
      <div className="flex size-full items-center justify-center bg-grey-lightest">
        <div className="flex flex-col items-center justify-center gap-y-8">
          <PersonLockRegular fontSize={72} className="text-grey-medium" />
          {isPending && (
            <div className="flex items-center gap-x-2">
              <SpinnerIosRegular fontSize={20} className="animate-spin" />
              <span>Please wait while we verify your account...</span>
            </div>
          )}
          {isIdle && (
            <div className="flex flex-col items-center gap-y-2 text-grey-medium">
              <span className="text-sm">You must be logged in to access this resource.</span>
              <span className="text-xl">
                <a className="font-bold underline" href={PWC_ID_CONFIG.authUrl(redirectPath)}>
                  Login and continue ?
                </a>
              </span>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        title="Unable to log in"
        content="Another session is possibly active. Would you like us to close all previous sessions try again? This will log you out on all other tabs and devices"
        isOpen={forceLoginDialogOpen}
        setIsOpen={setForceLoginDialogOpen}
        confirmText="Logout and try again"
        // onConfirm={() => login({ authCode, clientId, issuer, forceNewSession: true })}
        onConfirm={() => {
          window.location.replace(PWC_ID_CONFIG.authUrl(JSON.stringify({ forceNewSession: true })))
        }}
        onCancel={() => navigate({ pathname: '/workspaces', search: 'authStatus=login-failed' })}
      />
    </>
  )
}

LoginPage.displayName = 'LoginPage'
